import { useRef, useEffect, useState } from 'react';

export default function CameraCapture({ label, onCapture, onClose }) {
  const videoRef   = useRef(null);
  const canvasRef  = useRef(null);
  const streamRef  = useRef(null);
  const [ready, setReady]     = useState(false);
  const [error, setError]     = useState(null);
  const [captured, setCaptured] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // rear camera default

  useEffect(() => {
    startStream(facingMode);
    return () => stopStream();
  }, [facingMode]);

  function stopStream() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }

  async function startStream(mode) {
    stopStream();
    setError(null);
    setCaptured(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setReady(true);
        };
      }
    } catch (err) {
      setError('Camera not available: ' + (err.message || err.name));
    }
  }

  function capture() {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width  = video.videoWidth  || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    setCaptured(dataUrl);
    stopStream();
  }

  function retake() {
    setCaptured(null);
    startStream(facingMode);
  }

  function use() {
    if (captured) onCapture(captured);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) { stopStream(); onClose(); } }}>
      <div style={{
        width: 480, maxWidth: '96vw', background: 'var(--sur)',
        borderRadius: 'var(--rl)', overflow: 'hidden',
        boxShadow: '0 24px 60px rgba(0,0,0,.5)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>📷 Camera — {label}</div>
            <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>Position document clearly in frame, then capture</div>
          </div>
          <button onClick={() => { stopStream(); onClose(); }}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--tx3)' }}>×</button>
        </div>

        {/* Camera / preview area */}
        <div style={{ background: '#000', position: 'relative', aspectRatio: '4/3' }}>
          {!captured && (
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: ready ? 'block' : 'none' }} />
          )}
          {captured && (
            <img src={captured} alt="captured"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          )}
          {!ready && !captured && !error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12 }}>
              Starting camera...
            </div>
          )}
          {error && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📵</div>
              <div style={{ fontSize: 12, marginBottom: 6 }}>{error}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)' }}>Use the "Upload" button instead to select a file</div>
            </div>
          )}
          {/* Flip camera button (shown when live) */}
          {ready && !captured && (
            <button onClick={() => setFacingMode(m => m === 'environment' ? 'user' : 'environment')}
              style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,.5)', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: '#fff', fontSize: 11 }}>
              🔄 Flip
            </button>
          )}
          {/* Hidden canvas for snapshot */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        {/* Footer buttons */}
        <div style={{ padding: '12px 16px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--bd)' }}>
          <button className="btn btn-ghost" onClick={() => { stopStream(); onClose(); }}>Cancel</button>
          {!captured && !error && (
            <button className="btn btn-primary" onClick={capture} disabled={!ready}
              style={{ opacity: ready ? 1 : .5 }}>
              📸 Capture
            </button>
          )}
          {captured && (
            <>
              <button className="btn btn-ghost" onClick={retake}>🔄 Retake</button>
              <button className="btn btn-primary" style={{ background: 'var(--gr)', borderColor: 'var(--gr)' }} onClick={use}>
                ✅ Use Photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
